import { RouteReuseStrategy, DetachedRouteHandle, ActivatedRouteSnapshot, Route } from '@angular/router';
import { Injectable } from '@angular/core';
import { WindowService } from 'src/shared/providers/window.service';

@Injectable({ providedIn: 'root' })
export class CustomRouteReuseStrategy implements RouteReuseStrategy {
  private static instance: CustomRouteReuseStrategy;
  storedRoutes = new Map<string, DetachedRouteHandle>();
  // /overview is a different page for rental and other accounts, so a stored page
  // is only reattached to the same route entry it was detached from.
  private storedRouteConfigs = new Map<string, Route | null>();

  constructor(private windowService: WindowService) {
    CustomRouteReuseStrategy.instance = this;
  }

  static clearStoredRoutes(): void {
    if (CustomRouteReuseStrategy.instance) {
      CustomRouteReuseStrategy.instance.storedRoutes.clear();
      CustomRouteReuseStrategy.instance.storedRouteConfigs.clear();
    }
  }
  shouldDetachRoutes = [
    'overview',
    'schedule',
    'messages',
    'maintenance',
    'checklists',
    'restocking',
    'bills',
    'contacts',
    'my-pros',
    'automations/summary',
    'more',
    'agent',
    'properties',
    'finance',
  ]

  private getRoutePath(route: ActivatedRouteSnapshot): string {
    return route.pathFromRoot
      .flatMap(r => r.url.map(s => s.path))
      .filter(p => p)
      .join('/');
  }

  private getStoreKey(route: ActivatedRouteSnapshot): string {
    const candidates = [
      route.routeConfig?.path,
      route.url?.map(s => s.path).join('/'),
      this.getRoutePath(route),
    ].filter(Boolean);
    return candidates.find(p => this.shouldDetachRoutes.includes(p)) ?? this.getRoutePath(route);
  }

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    if (!this.windowService.isDesktopRes) {
      return false;
    }
    const key = this.getStoreKey(route);
    return this.shouldDetachRoutes.includes(key);
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
    const key = this.getStoreKey(route);
    if (handle) {
      const now = new Date().getTime();
      localStorage.setItem(`${key}.lastStore`, now.toString());
      this.storedRoutes.set(key, handle);
      this.storedRouteConfigs.set(key, route.routeConfig);
    } else {
      this.storedRoutes.delete(key);
      this.storedRouteConfigs.delete(key);
    }
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const key = this.getStoreKey(route);
    const lastStore = localStorage.getItem(`${key}.lastStore`);
    const hasStored = this.storedRoutes.has(key) && this.storedRouteConfigs.get(key) === route.routeConfig;
    if (lastStore && hasStored) {
      const timeSinceDetach = new Date().getTime() - parseInt(lastStore, 10);
      return timeSinceDetach < 3 * 60 * 60 * 1000;
    }
    return false;
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const key = this.getStoreKey(route);
    const handle = this.storedRoutes.get(key) || null;
    if (handle) {
      // Force layout recalculation after route reattachment.
      // Without this, OnPush components (e.g. tidy-table with virtual scroll)
      // may render empty until the user scrolls.
      setTimeout(() => window.dispatchEvent(new Event('resize')));
    }
    return handle;
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig;
  }
}
